import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  BarChart3,
  Bell,
  Box,
  CalendarDays,
  ClipboardList,
  CreditCard,
  Headphones,
  Home,
  LogOut,
  Menu,
  MessageSquareText,
  Package,
  Paperclip,
  Plus,
  QrCode,
  RefreshCw,
  ScanLine,
  Search,
  Settings,
  Truck,
  Users,
  Wrench,
  X,
} from "lucide-react";
import type { SessionPayload } from "./AuthenticatedApp";
import { Modal } from "./Modal";
import { MaintenanceWorkOrders } from "./MaintenanceWorkOrders";
import { PpmWorkspace } from "./PpmWorkspace";
import { HelpWorkspace } from "./HelpWorkspace";
import { AutonomousMaintenance } from "./AutonomousMaintenance";
import { HelpdeskWorkspace } from "./HelpdeskWorkspace";
import {
  AssetWorkspace,
  InventoryWorkspace,
  NotificationWorkspace,
  ReportsWorkspace,
} from "./CmmsAdminWorkspaces";
import { AttachmentWorkspace } from "./AttachmentWorkspace";
import { UserAdminWorkspace } from "./UserAdminWorkspace";
import { JobTypeSettings } from "./JobTypeSettings";
import { FeedbackWorkspace } from "./FeedbackWorkspace";
import { BillingWorkspace } from "./BillingWorkspace";
import { QrScanWorkspace } from "./QrScanWorkspace";
import { AppointmentsWorkspace } from "./AppointmentsWorkspace";
import { SupportAccessWorkspace } from "./SupportAccessWorkspace";
import { ShiftSettings } from "./ShiftSettings";
import { GroupPermissionSettings } from "./GroupPermissionSettings";
import { AutonomousGroupSettings } from "./AutonomousGroupSettings";
import { StocktakeWorkspace } from "./StocktakeWorkspace";
import { CmmsCalendar } from "./CmmsCalendar";
import { AuditLogWorkspace } from "./AuditLogWorkspace";

const apiOrigin = location.hostname.endsWith(".taskroute.co.uk")
  ? "https://taskroute.co.uk"
  : "";
const tenant = () => localStorage.getItem("taskroute-tenant") || "northstar";
export const request = (path: string, init: RequestInit = {}) =>
  fetch(`${apiOrigin}${path}`, {
    credentials: "include",
    ...init,
    headers: { "x-tenant-slug": tenant(), ...(init.headers || {}) },
  });

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

function FloatingFeedback({ section, product }: { section: string; product: string }) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("PROBLEM");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const page = `${product} › ${section}`;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!message.trim()) return;
    setBusy(true);
    try {
      await request("/api/feedback", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ category, message: message.trim(), page }),
      });
      setSent(true);
      setMessage("");
      setTimeout(() => { setSent(false); setOpen(false); }, 2500);
    } catch {}
    finally { setBusy(false); }
  }

  return (
    <div className="float-feedback">
      {open && (
        <div className="float-feedback-panel">
          <div className="float-feedback-header">
            <span>
              <MessageSquareText size={14} /> Alpha feedback
            </span>
            <button onClick={() => { setOpen(false); setSent(false); }}>✕</button>
          </div>
          <div className="float-feedback-page">📍 {page}</div>
          {sent ? (
            <div className="float-feedback-sent">✓ Sent — thank you!</div>
          ) : (
            <form onSubmit={submit}>
              <select value={category} onChange={e => setCategory(e.target.value)}>
                <option value="PROBLEM">Something didn't work</option>
                <option value="CONFUSING">This was confusing</option>
                <option value="SUGGESTION">Feature suggestion</option>
                <option value="OTHER">Other feedback</option>
              </select>
              <textarea
                autoFocus
                required
                minLength={3}
                rows={4}
                placeholder="What happened? What were you trying to do?"
                value={message}
                onChange={e => setMessage(e.target.value)}
              />
              <button disabled={busy || !message.trim()}>{busy ? "Sending…" : "Send feedback"}</button>
            </form>
          )}
        </div>
      )}
      <button
        className="float-feedback-btn"
        onClick={() => setOpen(o => !o)}
        title="Send feedback"
      >
        <MessageSquareText size={18} />
        {!open && <span>Feedback</span>}
      </button>
    </div>
  );
}

export async function downloadProtected(path: string, filename: string) {
  const response = await request(path);
  if (!response.ok) throw new Error("Unable to generate QR label");
  const href = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(href);
}
const maintenanceSections = [
  ["Overview", Home],
  ["Calendar", CalendarDays],
  ["Work orders", ClipboardList],
  ["PPM", CalendarDays],
  ["Autonomous", ClipboardList],
  ["Autonomous setup", Settings],
  ["Assets", Box],
  ["Inventory", Package],
  ["Stocktake", ScanLine],
  ["Attachments", Paperclip],
  ["Helpdesk", Headphones],
  ["Notifications", Bell],
  ["Reports", BarChart3],
  ["Feedback", MessageSquareText],
  ["Scan", QrCode],
  ["Help", Headphones],
] as const;
const dispatchSections = [
  ["Overview", Home],
  ["Dispatch orders", Truck],
  ["Manifests", ClipboardList],
  ["Reports", BarChart3],
  ["Help", Headphones],
] as const;
const appointmentSections = [["Appointments", CalendarDays],["Help", Headphones]] as const;
type Dashboard = {
  workOrders: any[];
  parts: any[];
  occurrences: any[];
  tickets: any[];
  orders: any[];
  products?: ("MAINTENANCE" | "DISPATCH" | "APPOINTMENTS")[];
};
type Asset = {
  id: string;
  name: string;
  type: string;
  path: string;
  externalRef?: string;
};

export function FunctionalApp({ session }: { session: SessionPayload }) {
  const initialScan = new URLSearchParams(location.search).get("scan") || "";
  const initialProduct = session.user.products.includes("MAINTENANCE")
    ? "MAINTENANCE"
    : session.user.products.includes("DISPATCH")
      ? "DISPATCH"
      : "APPOINTMENTS";
  const [product, setProduct] = useState<
    "MAINTENANCE" | "DISPATCH" | "APPOINTMENTS"
  >(initialProduct);
  const [section, setSection] = useState(initialScan ? "Scan" : (session.user.permissions.includes('maintenance.autonomous.manage') || session.user.permissions.includes('maintenance.autonomous.complete')) && !session.user.permissions.includes('maintenance.ppm.manage') ? 'Autonomous' : "Overview");
  const [data, setData] = useState<Dashboard>({
    workOrders: [],
    parts: [],
    occurrences: [],
    tickets: [],
    orders: [],
  });
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [mobile, setMobile] = useState(false);
  const [create, setCreate] = useState(false);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [scanTarget, setScanTarget] = useState<{
    type: string;
    id: string;
  } | null>(null);
  const canAdmin =
    session.user.isTenantOwner ||
    session.user.permissions.includes("*") ||
    session.user.permissions.includes("platform.users.manage");
  const sections = [
    ...(product === "MAINTENANCE"
      ? maintenanceSections.filter(([name]) => {
          const permission = (key:string) => session.user.isTenantOwner || session.user.permissions.includes('*') || session.user.permissions.includes(key);
          if(name==='Work orders') return permission('maintenance.work_orders.claim') || permission('maintenance.work_orders.complete') || permission('maintenance.work_orders.assign');
          if(name==='PPM') return permission('maintenance.ppm.manage');
          if(name==='Autonomous setup') return permission('maintenance.autonomous.manage');
          if(name==='Autonomous') return permission('maintenance.autonomous.manage') || permission('maintenance.autonomous.complete');
          if(name==='Inventory') return permission('maintenance.inventory.manage');
          if(name==='Stocktake') return permission('maintenance.inventory.manage');
          if(session.user.permissions.includes('maintenance.autonomous.manage') && !permission('maintenance.ppm.manage') && ['Overview','Assets','Attachments','Reports','Scan'].includes(name)) return false;
          return true;
        })
      : product === "DISPATCH"
        ? dispatchSections
        : appointmentSections),
    ...(canAdmin ? [["Admin", Settings] as const] : []),
    ...(session.user.isTenantOwner ? [["Billing", CreditCard] as const] : []),
  ];
  async function load() {
    setLoading(true);
    setError("");
    try {
      const productionFocused=(session.user.permissions.includes('maintenance.autonomous.manage') || session.user.permissions.includes('maintenance.autonomous.complete')) && !session.user.permissions.includes('maintenance.ppm.manage');
      if(!productionFocused){const d = await request("/api/dashboard"); if (!d.ok) throw new Error(); setData(await d.json());}
      if (session.user.products.includes("MAINTENANCE") && !productionFocused) {
        const a = await request("/api/assets");
        if (a.ok) setAssets(await a.json());
      }
    } catch {
      setError("Unable to load tenant data.");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
    registerPush();
  }, []);

  async function registerPush() {
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
      const reg = await navigator.serviceWorker.ready;
      const { publicKey } = await request("/api/push/vapid-public-key").then(r => r.json()).catch(() => ({}));
      if (!publicKey) return;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        const permission = await Notification.requestPermission();
        if (permission !== "granted") return;
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }
      await request("/api/push/subscribe", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(sub.toJSON()),
      });
    } catch {}
  }

  const filtered = useMemo(
    () =>
      data.workOrders.filter((w) =>
        `${w.number} ${w.title} ${w.status}`
          .toLowerCase()
          .includes(query.toLowerCase()),
      ),
    [data.workOrders, query],
  );
  async function logout() {
    await request("/api/auth/logout", { method: "POST" });
    location.reload();
  }
  async function resetAccounts() {
    if (
      !confirm(
        "Reset all Northstar demo accounts to a fresh shared demo password?",
      )
    )
      return;
    const r = await request("/api/admin/demo-accounts/reset", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ password: "TaskRouteDemo!2026" }),
    });
    if (r.ok) setAccounts((await r.json()).accounts);
    else
      setError(
        "Only the seeded Northstar administrator can reset demo accounts.",
      );
  }
  function switchProduct(next: "MAINTENANCE" | "DISPATCH" | "APPOINTMENTS") {
    setProduct(next);
    setSection(next === "APPOINTMENTS" ? "Appointments" : "Overview");
    setMobile(false);
  }
  const initials = session.user.name
    .split(/\s+/)
    .map((x) => x[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <div className="func-app">
      <div
        className={`func-scrim ${mobile ? "show" : ""}`}
        onClick={() => setMobile(false)}
      />
      <aside className={mobile ? "open" : ""}>
        <div className="brand">
          <span className="mark">◆</span>TaskRoute
          <button className="close" onClick={() => setMobile(false)}>
            <X />
          </button>
        </div>
        <div className="func-tenant">
          <small>Company</small>
          <strong>{session.tenant.name}</strong>
        </div>
        <nav>
          {sections.map(([name, Icon]) => (
            <button
              key={name}
              className={section === name ? "active" : ""}
              onClick={() => {
                setSection(name);
                setMobile(false);
              }}
            >
              <Icon />
              {name}
            </button>
          ))}
        </nav>
        <div className="side-foot">
          <button onClick={logout}>
            <LogOut />
            Sign out
          </button>
        </div>
      </aside>
      <main>
        <header className="func-top">
          <button className="menu" onClick={() => setMobile(true)}>
            <Menu />
          </button>
          <div>
            <b>{section}</b>
            <small>{session.tenant.name}</small>
          </div>
          {session.user.products.includes("MAINTENANCE") ? (
            <button
              className={`func-product ${product === "MAINTENANCE" ? "selected" : ""}`}
              onClick={() => switchProduct("MAINTENANCE")}
            >
              <Wrench />
              Maintenance
            </button>
          ) : null}
          {session.user.products.includes("DISPATCH") ? (
            <button
              className={`func-product ${product === "DISPATCH" ? "selected" : ""}`}
              onClick={() => switchProduct("DISPATCH")}
            >
              <Truck />
              Dispatch
            </button>
          ) : null}
          {session.user.products.includes("APPOINTMENTS") ? (
            <button
              className={`func-product ${product === "APPOINTMENTS" ? "selected" : ""}`}
              onClick={() => switchProduct("APPOINTMENTS")}
            >
              <CalendarDays />
              Appointments
            </button>
          ) : null}
          <div className="func-user" title={session.user.name}>
            {initials}
          </div>
        </header>
        <div className="func-content">
          {error ? (
            <div className="func-error">
              {error}
              <button onClick={load}>Retry</button>
            </div>
          ) : null}
          {loading ? (
            <div className="func-loading">Loading workspace…</div>
          ) : (
            <Screen
              session={session}
              product={product}
              section={section}
              data={data}
              assets={assets}
              filtered={filtered}
              query={query}
              setQuery={setQuery}
              setCreate={setCreate}
              setSection={setSection}
              resetAccounts={resetAccounts}
              accounts={accounts}
              reload={load}
              scanTarget={scanTarget}
              initialScan={initialScan}
              openScanTarget={(type: string, id: string) => {
                setScanTarget({ type, id });
                setSection(
                  type === "PART"
                    ? "Inventory"
                    : type === "ASSET"
                      ? "Assets"
                      : "Work orders",
                );
              }}
            />
          )}
          <FloatingFeedback section={section} product={product} />
        </div>
      </main>
      {create && product === "MAINTENANCE" ? (
        <CreateWorkOrder
          assets={assets}
          close={() => setCreate(false)}
          saved={() => {
            setCreate(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}

function Screen(p: any) {
  if (p.product === "APPOINTMENTS" && p.section === "Appointments")
    return <AppointmentsWorkspace />;
  if (
    p.product === "DISPATCH" &&
    !["Reports", "Admin", "Manifests", "Billing", "Help"].includes(p.section)
  )
    return <DispatchWorkspace />;
  if (p.section === "Manifests") return <ManifestWorkspace />;
  if (p.section === "Calendar") return <CmmsCalendar />;
  if (p.section === "Overview")
    return (
      <>
        <div className="func-heading">
          <div>
            <h1>Maintenance overview</h1>
            <p>Live maintenance workload for this workspace.</p>
          </div>
          <button onClick={() => p.setCreate(true)}>
            <Plus />
            Create work order
          </button>
        </div>
        <div className="func-kpis">
          <Kpi
            label="Open work orders"
            value={
              p.data.workOrders.filter((w: any) => w.status !== "COMPLETED")
                .length
            }
          />
          <Kpi
            label="Production stopped"
            value={
              p.data.workOrders.filter(
                (w: any) => w.priority === "PRODUCTION_STOPPED",
              ).length
            }
          />
          <Kpi label="Assets" value={p.assets.length} />
          <Kpi label="Parts" value={p.data.parts.length} />
        </div>
        {p.session.user.isTenantOwner &&
          p.data.workOrders.length === 0 &&
          p.assets.length === 0 && (
          <section className="func-panel onboarding-checklist">
            <div className="func-table-head"><h2>🚀 Get started with TaskRoute</h2></div>
            <div className="onboarding-steps">
              {[
                { label: "Add your first asset", desc: "Build your asset hierarchy — sites, areas, machines and components.", section: "Assets", done: p.assets.length > 0 },
                { label: "Set up your team", desc: "Create user accounts for engineers, managers and store operatives.", section: "Admin", done: false },
                { label: "Add spare parts to inventory", desc: "Record your parts catalogue with bin locations and reorder levels.", section: "Inventory", done: p.data.parts.length > 0 },
                { label: "Create a PPM schedule", desc: "Set up planned preventive maintenance for your key assets.", section: "PPM", done: p.data.occurrences.length > 0 },
                { label: "Raise your first work order", desc: "Log a fault or planned job to see the full maintenance workflow.", section: "Work orders", done: p.data.workOrders.length > 0 },
              ].map((step) => (
                <div key={step.label} className={`onboarding-step ${step.done ? "done" : ""}`} onClick={() => !step.done && p.setSection && p.setSection(step.section)}>
                  <span className="onboarding-tick">{step.done ? "✓" : "○"}</span>
                  <span>
                    <b>{step.label}</b>
                    <small>{step.desc}</small>
                  </span>
                  {!step.done && <span className="onboarding-go">→</span>}
                </div>
              ))}
            </div>
          </section>
        )}
        <WorkTable rows={p.filtered} query={p.query} setQuery={p.setQuery} />
      </>
    );
  if (p.section === "Work orders")
    return (
      <MaintenanceWorkOrders
        initialId={
          p.scanTarget?.type === "WORK_ORDER" ? p.scanTarget.id : undefined
        }
      />
    );
  if (p.section === "PPM") return <PpmWorkspace />;
  if (p.section === "Autonomous setup") return <PpmWorkspace autonomousOnly />;
  if (p.section === "Autonomous") return <AutonomousMaintenance />;
  if (p.section === "Help") return <HelpWorkspace session={p.session} />;
  if (p.section === "Assets")
    return (
      <AssetWorkspace
        initialId={p.scanTarget?.type === "ASSET" ? p.scanTarget.id : undefined}
      />
    );
  if (p.section === "Inventory")
    return (
      <InventoryWorkspace
        initialId={p.scanTarget?.type === "PART" ? p.scanTarget.id : undefined}
      />
    );
  if (p.section === "Stocktake") return <StocktakeWorkspace />;
  if (p.section === "Attachments") return <AttachmentWorkspace />;
  if (p.section === "Helpdesk") return <HelpdeskWorkspace />;
  if (p.section === "Dispatch orders")
    return <DispatchOrders orders={p.data.orders} />;
  if (p.section === "Notifications") return <NotificationWorkspace />;
  if (p.section === "Feedback") return <FeedbackWorkspace />;
  if (p.section === "Scan")
    return (
      <QrScanWorkspace onOpen={p.openScanTarget} initialCode={p.initialScan} />
    );
  if (p.section === "Reports")
    return p.product === "MAINTENANCE" ? (
      <ReportsWorkspace />
    ) : (
      <DispatchReports />
    );
  if (p.section === "Admin")
    return (
      <>
        <BrandingSettings />
        {p.product === "MAINTENANCE" ? <><JobTypeSettings /><ShiftSettings /><AutonomousGroupSettings /></> : null}
        {p.product === "DISPATCH" ? <DispatchRouteSettings /> : null}
        <UserAdminWorkspace />
        <GroupPermissionSettings />
        <SupportAccessWorkspace tickets={p.data.tickets} />
        <AuditLogWorkspace />
      </>
    );
  if (p.section === "Billing") return <BillingWorkspace />;
  return null;
}

function DispatchRouteSettings() {
  const [routes, setRoutes] = useState<any[]>([]);
  const [form, setForm] = useState({ code: "", name: "", description: "" });
  const [editId, setEditId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: "", description: "" });
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    const r = await request("/api/dispatch/route-codes");
    if (r.ok) { const d = await r.json(); setRoutes(d.routes || []); }
  }
  useEffect(() => { load(); }, []);

  async function create(e: React.FormEvent) {
    e.preventDefault(); setBusy(true); setMessage("");
    const r = await request("/api/dispatch/route-codes", {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify(form),
    });
    const x = await r.json();
    if (r.ok) { setForm({ code: "", name: "", description: "" }); load(); setMessage(`Route ${form.code} added.`); }
    else setMessage(String(x.error).replaceAll("_", " "));
    setBusy(false);
  }

  async function update(id: string) {
    setBusy(true);
    const r = await request(`/api/dispatch/route-codes/${id}`, {
      method: "PUT", headers: { "content-type": "application/json" },
      body: JSON.stringify(editForm),
    });
    if (r.ok) { setEditId(null); load(); setMessage("Route updated."); }
    else { const x = await r.json(); setMessage(String(x.error).replaceAll("_", " ")); }
    setBusy(false);
  }

  async function remove(id: string, code: string) {
    if (!confirm(`Deactivate route ${code}? It won't be available for new orders but existing orders are unaffected.`)) return;
    const r = await request(`/api/dispatch/route-codes/${id}`, { method: "DELETE" });
    const x = await r.json();
    if (r.ok) { load(); setMessage(`Route ${code} deactivated.`); }
    else setMessage(String(x.error).replaceAll("_", " "));
  }

  return (
    <section className="func-panel" style={{ marginBottom: 16 }}>
      <div className="func-table-head">
        <div>
          <h2>Dispatch route codes</h2>
          <p style={{ margin: "3px 0 0", fontSize: 12, color: "var(--muted)" }}>
            Manage the route codes available when creating dispatch orders.
          </p>
        </div>
      </div>
      {message && <div className="func-admin-note">{message}</div>}
      <form className="simple-form" style={{ padding: "14px 20px 0" }} onSubmit={create}>
        <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 1.5fr auto", gap: 10, alignItems: "end" }}>
          <label>Code<input required maxLength={20} placeholder="e.g. AD" value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} /></label>
          <label>Name<input required maxLength={80} placeholder="e.g. Alldays Domestic" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></label>
          <label>Description (optional)<input maxLength={255} placeholder="e.g. UK domestic palletised delivery" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></label>
          <button disabled={busy} style={{ marginBottom: 0 }}>Add route</button>
        </div>
      </form>
      <div style={{ marginTop: 14 }}>
        {routes.length === 0 ? <p className="func-empty">No route codes configured. Add one above.</p> : (
          <div className="func-list" style={{ "--cols": "100px 1fr 1.5fr 80px auto" } as any}>
            <div className="func-list-row func-th"><span>Code</span><span>Name</span><span>Description</span><span>Status</span><span></span></div>
            {routes.map((r: any) => (
              <div className="func-list-row" key={r.id}>
                {editId === r.id ? (
                  <>
                    <span><b>{r.code}</b></span>
                    <span><input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} style={{ width: "100%" }} /></span>
                    <span><input value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} style={{ width: "100%" }} /></span>
                    <span></span>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button disabled={busy} onClick={() => update(r.id)}>Save</button>
                      <button className="secondary" onClick={() => setEditId(null)}>Cancel</button>
                    </div>
                  </>
                ) : (
                  <>
                    <span><b>{r.code}</b></span>
                    <span>{r.name}</span>
                    <span style={{ color: "var(--muted)", fontSize: 12 }}>{r.description || "—"}</span>
                    <span className={r.active ? "active" : "inactive"}>{r.active ? "Active" : "Inactive"}</span>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button className="secondary" onClick={() => { setEditId(r.id); setEditForm({ name: r.name, description: r.description || "" }); }}>Edit</button>
                      {r.active && <button className="secondary" onClick={() => remove(r.id, r.code)}>Deactivate</button>}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function HoldingAreasWorkspace({ setMessage }: any) {
  const [areas, setAreas] = useState<any[]>([]);
  const [unassigned, setUnassigned] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name:"", code:"", type:"GENERAL", routeCodes:"", capacity:"50", notes:"" });
  const [editId, setEditId] = useState<string|null>(null);
  const [editForm, setEditForm] = useState<any>({});
  const [busy, setBusy] = useState(false);

  async function load() {
    const r = await request("/api/dispatch/holding-areas");
    if (r.ok) { const d = await r.json(); setAreas(d.areas||[]); setUnassigned(d.unassigned||[]); }
  }
  useEffect(() => { load(); }, []);

  async function create(e: React.FormEvent) {
    e.preventDefault(); setBusy(true);
    const r = await request("/api/dispatch/holding-areas", { method:"POST", headers:{"content-type":"application/json"}, body:JSON.stringify({...form, capacity: Number(form.capacity)}) });
    if (r.ok) { setShowForm(false); setForm({name:"",code:"",type:"GENERAL",routeCodes:"",capacity:"50",notes:""}); load(); setMessage("Holding area created."); }
    else { const d = await r.json(); setMessage(String(d.error).replaceAll("_"," ")); }
    setBusy(false);
  }

  async function update(id: string) {
    setBusy(true);
    await request(`/api/dispatch/holding-areas/${id}`, { method:"PUT", headers:{"content-type":"application/json"}, body:JSON.stringify({...editForm, capacity: Number(editForm.capacity)}) });
    setEditId(null); load(); setMessage("Holding area updated.");
    setBusy(false);
  }

  async function remove(id: string, name: string) {
    if (!confirm(`Deactivate ${name}? It must be empty first.`)) return;
    const r = await request(`/api/dispatch/holding-areas/${id}`, { method:"DELETE" });
    if (r.ok) { load(); setMessage(`${name} deactivated.`); }
    else { const d = await r.json(); setMessage(String(d.error).replaceAll("_"," ")); }
  }

  async function release(sscc: string) {
    const r = await request(`/api/dispatch/pallets/${sscc}/release`, { method:"POST" });
    if (r.ok) { load(); setMessage(`${sscc} released to pick queue.`); }
    else { const d = await r.json(); setMessage(String(d.error).replaceAll("_"," ")); }
  }

  const typeLabels: Record<string,string> = { EXPORT:"Export", COLLECTION:"Collection", DOMESTIC:"Domestic", GENERAL:"General" };
  const typeColors: Record<string,string> = { EXPORT:"#b05810", COLLECTION:"#0a6e94", DOMESTIC:"#156e40", GENERAL:"#102235" };

  return (
    <>
      <div className="func-heading">
        <div><h1>Holding areas</h1><p>Named staging locations for pallets awaiting collection or loading.</p></div>
        <button onClick={() => setShowForm(!showForm)}><Plus />New area</button>
      </div>

      {showForm && (
        <Modal title="Create holding area" onClose={() => setShowForm(false)}>
          <form onSubmit={create}>
            <div style={{display:"grid",gridTemplateColumns:"120px 1fr 140px",gap:10}}>
              <label>Code<input required maxLength={20} placeholder="e.g. EXPORT-1" value={form.code} onChange={e=>setForm({...form,code:e.target.value.toUpperCase()})} /></label>
              <label>Name<input required maxLength={80} placeholder="e.g. Weekly Export Hold" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} /></label>
              <label>Capacity<input type="number" min="1" max="9999" value={form.capacity} onChange={e=>setForm({...form,capacity:e.target.value})} /></label>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <label>Type<select value={form.type} onChange={e=>setForm({...form,type:e.target.value})}>
                <option value="GENERAL">General</option>
                <option value="EXPORT">Export</option>
                <option value="COLLECTION">Collection</option>
                <option value="DOMESTIC">Domestic</option>
              </select></label>
              <label>Route codes (optional)<input maxLength={255} placeholder="e.g. AD, AL" value={form.routeCodes} onChange={e=>setForm({...form,routeCodes:e.target.value})} /></label>
            </div>
            <label>Notes (optional)<input maxLength={255} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} /></label>
            <button disabled={busy}>Create</button>
          </form>
        </Modal>
      )}

      {areas.length === 0 && !showForm && (
        <section className="func-panel"><p className="func-empty">No holding areas configured. Create one above to start staging pallets.</p></section>
      )}

      {areas.map((area: any) => (
        <section key={area.id} className="func-panel" style={{marginBottom:12}}>
          <div className="func-table-head">
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <span style={{background:typeColors[area.type]||"#102235",color:"#fff",borderRadius:6,padding:"4px 10px",fontWeight:800,fontSize:12,letterSpacing:.5}}>{area.code}</span>
              {editId===area.id ? (
                <input value={editForm.name} onChange={e=>setEditForm({...editForm,name:e.target.value})} style={{fontWeight:700,fontSize:15}} />
              ) : (
                <b style={{fontSize:15}}>{area.name}</b>
              )}
              <small style={{color:"var(--muted)"}}>{typeLabels[area.type]} · {area.pallets?.length||0}/{area.capacity} pallets</small>
            </div>
            <div style={{display:"flex",gap:8}}>
              {editId===area.id ? (
                <>
                  <button disabled={busy} onClick={()=>update(area.id)}>Save</button>
                  <button className="secondary" onClick={()=>setEditId(null)}>Cancel</button>
                </>
              ) : (
                <>
                  <a href={`/api/dispatch/holding-areas/${area.id}/label.svg`} target="_blank" className="secondary" style={{padding:"7px 12px",border:"1px solid #dce6ea",borderRadius:6,fontSize:12,textDecoration:"none",color:"#506573"}}>Print label</a>
                  <button className="secondary" onClick={()=>{setEditId(area.id);setEditForm({name:area.name,type:area.type,routeCodes:area.route_codes||"",capacity:area.capacity,notes:area.notes||""});}}>Edit</button>
                  {(area.pallets?.length||0)===0 && <button className="secondary" onClick={()=>remove(area.id,area.name)}>Deactivate</button>}
                </>
              )}
            </div>
          </div>
          {area.route_codes && <p style={{margin:"4px 20px 8px",fontSize:12,color:"var(--muted)"}}>Routes: {area.route_codes}</p>}
          {area.pallets?.length > 0 ? (
            <div className="func-list" style={{"--cols":"140px 1fr 120px 100px auto"} as any}>
              <div className="func-list-row func-th"><span>SSCC</span><span>Order</span><span>Route</span><span>Held</span><span></span></div>
              {area.pallets.map((p: any) => (
                <div key={p.id} className="func-list-row">
                  <span className="mono">{p.sscc}</span>
                  <span><b>{p.order_number}</b><small>{p.customer}</small></span>
                  <span>{p.route_code}</span>
                  <span style={{color: Number(p.hours_held)>48?"#c0392b":Number(p.hours_held)>24?"#e67e22":"var(--muted)",fontWeight:Number(p.hours_held)>24?700:400}}>
                    {Number(p.hours_held)<1?"<1h":`${p.hours_held}h`}
                  </span>
                  <button className="secondary" onClick={()=>release(p.sscc)}>Release</button>
                </div>
              ))}
            </div>
          ) : (
            <p className="func-empty" style={{padding:"12px 20px"}}>Empty</p>
          )}
        </section>
      ))}

      {unassigned.length > 0 && (
        <section className="func-panel">
          <div className="func-table-head"><h2>Unassigned holding pallets</h2></div>
          <div className="func-list" style={{"--cols":"140px 1fr 120px auto"} as any}>
            <div className="func-list-row func-th"><span>SSCC</span><span>Order</span><span>Route</span><span></span></div>
            {unassigned.map((p: any) => (
              <div key={p.id} className="func-list-row">
                <span className="mono">{p.sscc}</span>
                <span><b>{p.order_number}</b><small>{p.customer}</small></span>
                <span>{p.route_code}</span>
                <button className="secondary" onClick={()=>release(p.sscc)}>Release</button>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}

function ManifestWorkspace() {
  const [loads, setLoads] = useState<any[]>([]),
    [loadId, setLoadId] = useState(""),
    [detail, setDetail] = useState<any>(null),
    [history, setHistory] = useState<any[]>([]),
    [dialog, setDialog] = useState<any>(null),
    [message, setMessage] = useState("");
  async function refresh(preferred = loadId) {
    const [l, m] = await Promise.all([
      request("/api/dispatch/load-state"),
      request("/api/dispatch/manifests"),
    ]);
    const loadRows = l.ok ? (await l.json()).loads : [],
      manifestRows = m.ok ? (await m.json()).manifests : [];
    setLoads(loadRows);
    setHistory(manifestRows);
    const selected = preferred || loadRows[0]?.id || "";
    setLoadId(selected);
    if (selected) {
      const d = await request(`/api/dispatch/loads/${selected}/manifest`);
      if (d.ok) setDetail(await d.json());
    }
  }
  useEffect(() => {
    refresh();
  }, []);
  async function action(path: string, body: any = {}) {
    const r = await request(path, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      }),
      x = await r.json();
    setMessage(x.message || x.error || "Saved");
    if (r.ok) await refresh(loadId);
  }
  async function remove(p: any) {
    setDialog({ type: "remove", pallet: p });
  }
  async function printManifest(id: string) {
    const r = await request(`/api/dispatch/manifests/${id}/print`);
    if (!r.ok) return setMessage("Unable to generate manifest");
    const url = URL.createObjectURL(
      new Blob([await r.text()], { type: "text/html" }),
    );
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }
  const current = detail?.current;
  return (
    <>
      <div className="func-heading">
        <div>
          <h1>Trailer manifests</h1>
          <p>
            Review, approve, sign and close one uniquely identified trailer.
          </p>
        </div>
      </div>
      {message ? <div className="func-admin-note">{message}</div> : null}
      <section className="func-panel manifest-selector">
        <label>
          Active trailer
          <select
            value={loadId}
            onChange={(e) => {
              setLoadId(e.target.value);
              refresh(e.target.value);
            }}
          >
            <option value="">Select trailer</option>
            {loads.map((x) => (
              <option key={x.id} value={x.id}>
                {x.destination_code} · {x.trailer_number} · {x.status}
              </option>
            ))}
          </select>
        </label>
      </section>
      {detail ? (
        <>
          <div className="manifest-summary">
            <Kpi label="Pallets" value={detail.totals.pallets} />
            <Kpi label="Orders" value={detail.totals.orders} />
            <Kpi label="Boxes" value={detail.totals.boxes} />
            <Kpi label="Weight kg" value={detail.totals.weightKg} />
          </div>
          <section className="func-panel manifest-actions">
            <div>
              <strong>
                {detail.load.destination_code} · Trailer{" "}
                {detail.load.trailer_number}
              </strong>
              <span>Load status: {detail.load.status}</span>
              <span>Manifest: {current?.status || "Not prepared"}</span>
            </div>
            {detail.load.status === "LOADING" ? (
              <button
                onClick={() => action(`/api/dispatch/loads/${loadId}/full`)}
              >
                Mark trailer Full
              </button>
            ) : null}
            {detail.load.status === "FULL" && !current ? (
              <button
                onClick={() =>
                  action(`/api/dispatch/loads/${loadId}/manifest/draft`)
                }
              >
                Prepare draft manifest
              </button>
            ) : null}
            {current?.status === "DRAFT" ? (
              <button
                onClick={() =>
                  action(`/api/dispatch/manifests/${current.id}/approve`)
                }
              >
                Approve manifest
              </button>
            ) : null}
            {current?.status === "APPROVED" ? (
              <DriverSignature
                manifestId={current.id}
                completed={() => refresh(loadId)}
                setMessage={setMessage}
              />
            ) : null}
            {current?.status === "SIGNED" ? (
              <button
                className="danger"
                onClick={() => setDialog({ type: "close", manifest: current })}
              >
                Close trailer
              </button>
            ) : null}
            {current ? (
              <button onClick={() => printManifest(current.id)}>
                Print manifest
              </button>
            ) : null}
          </section>
          <section className="func-panel">
            <div className="func-table-head">
              <h2>Manifest contents</h2>
            </div>
            <div className="manifest-pallets">
              {detail.pallets.map((p: any) => (
                <div key={p.pallet_id}>
                  <span>
                    <strong>{p.sscc}</strong>
                    <small>
                      {p.order_number} · {p.customer}
                    </small>
                  </span>
                  <span>{p.boxes} boxes</span>
                  <span>{p.total_weight_kg} kg</span>
                  {!["CLOSED"].includes(current?.status) &&
                  detail.load.status !== "CLOSED" ? (
                    <button onClick={() => remove(p)}>Remove</button>
                  ) : null}
                </div>
              ))}
            </div>
          </section>
        </>
      ) : null}
      <section className="func-panel manifest-history">
        <div className="func-table-head">
          <h2>Manifest history</h2>
        </div>
        {history.map((m) => (
          <div key={m.id}>
            <span>
              <strong>{m.reference}</strong>
              <small>
                {m.destination_code} · {m.trailer_number}
              </small>
            </span>
            <em>{m.status}</em>
            <span>{m.driver_name || "Unsigned"}</span>
            <button onClick={() => printManifest(m.id)}>Print</button>
            {m.status === "CLOSED" ? (
              <button
                onClick={() => setDialog({ type: "reopen", manifest: m })}
              >
                Reopen
              </button>
            ) : null}
          </div>
        ))}
      </section>
      {dialog ? (
        <ManifestActionDialog
          dialog={dialog}
          close={() => setDialog(null)}
          save={async (value: string) => {
            if (dialog.type === "remove") {
              await action(`/api/dispatch/loads/${loadId}/pallets/${dialog.pallet.pallet_id}/remove`, { reason: value });
            } else if (dialog.type === "reopen") {
              await action(`/api/dispatch/manifests/${dialog.manifest.id}/reopen`, { reason: value });
            } else {
              const recipients = value.split(",").map((x) => x.trim()).filter(Boolean);
              await action(`/api/dispatch/manifests/${dialog.manifest.id}/close`, { recipients });
            }
            setDialog(null);
          }}
        />
      ) : null}
    </>
  );
}
function ManifestActionDialog({ dialog, close, save }: any) {
  const closing = dialog.type === "close";
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const title = closing ? "Close and dispatch trailer" : dialog.type === "remove" ? `Remove pallet ${dialog.pallet.sscc}` : "Reopen closed trailer";
  return <div className="cmms-modal" role="dialog" aria-modal="true"><form className="cmms-temp-form" onSubmit={async(e)=>{e.preventDefault();setBusy(true);await save(value.trim());setBusy(false)}}><div><h2>{title}</h2><p>{closing ? "This closes the manifest and dispatches every assigned pallet. Email delivery is optional." : "A reason is required and will be retained in the audit history."}</p></div><label>{closing ? "Manifest email recipients (optional, comma-separated)" : "Reason"}{closing ? <input type="text" value={value} onChange={e=>setValue(e.target.value)} placeholder="transport@example.com, customer@example.com"/> : <textarea required minLength={3} rows={4} value={value} onChange={e=>setValue(e.target.value)}/>}</label><div className="cmms-modal-actions"><button type="button" className="secondary" onClick={close}>Cancel</button><button className={closing ? "danger" : ""} disabled={busy}>{busy ? "Saving…" : closing ? "Close trailer" : "Confirm"}</button></div></form></div>;
}
function DriverSignature({ manifestId, completed, setMessage }: any) {
  const canvas = useRef<HTMLCanvasElement>(null),
    drawing = useRef(false),
    [driverName, setDriverName] = useState(""),
    [registration, setRegistration] = useState("");
  function point(e: React.PointerEvent<HTMLCanvasElement>) {
    const c = canvas.current!,
      r = c.getBoundingClientRect();
    return [
      (e.clientX - r.left) * (c.width / r.width),
      (e.clientY - r.top) * (c.height / r.height),
    ];
  }
  function down(e: React.PointerEvent<HTMLCanvasElement>) {
    drawing.current = true;
    const [x, y] = point(e),
      ctx = canvas.current!.getContext("2d")!;
    ctx.beginPath();
    ctx.moveTo(x, y);
    e.currentTarget.setPointerCapture(e.pointerId);
  }
  function move(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    const [x, y] = point(e),
      ctx = canvas.current!.getContext("2d")!;
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#102f42";
    ctx.lineTo(x, y);
    ctx.stroke();
  }
  function clear() {
    canvas
      .current!.getContext("2d")!
      .clearRect(0, 0, canvas.current!.width, canvas.current!.height);
  }
  async function sign(e: FormEvent) {
    e.preventDefault();
    const r = await request(`/api/dispatch/manifests/${manifestId}/sign`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          driverName,
          vehicleRegistration: registration,
          signatureData: canvas.current!.toDataURL("image/png"),
        }),
      }),
      x = await r.json();
    setMessage(r.ok ? "Driver signature saved" : x.error);
    if (r.ok) completed();
  }
  return (
    <form className="signature-form" onSubmit={sign}>
      <input
        placeholder="Driver name"
        value={driverName}
        onChange={(e) => setDriverName(e.target.value)}
        required
      />
      <input
        placeholder="Vehicle registration"
        value={registration}
        onChange={(e) => setRegistration(e.target.value.toUpperCase())}
        required
      />
      <canvas
        ref={canvas}
        width="700"
        height="180"
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={() => (drawing.current = false)}
        onPointerCancel={() => (drawing.current = false)}
      />
      <button type="button" onClick={clear}>
        Clear
      </button>
      <button>Save signature</button>
    </form>
  );
}
function DispatchWorkspace() {
  const [t, setT] = useState("Overview"),
    [data, setData] = useState<any>({
      orders: [],
      pallets: [],
      jobs: [],
      loads: [],
    }),
    [message, setMessage] = useState("");
  async function load() {
    const [r, l] = await Promise.all([
      request("/api/dispatch/bootstrap"),
      request("/api/dispatch/load-state"),
    ]);
    if (r.ok) {
      const next = await r.json();
      if (l.ok) next.loads = (await l.json()).loads;
      setData(next);
    } else setMessage("Your account does not have Dispatch access.");
  }
  useEffect(() => {
    load();
  }, []);
  const tabs = [
    "Overview",
    "Order input",
    "Pick queue",
    "Runner queue",
    "Holding areas",
    "Loads",
    "Order search",
  ];
  return (
    <>
      <div className="dispatch-tabs">
        {tabs.map((x) => (
          <button
            className={t === x ? "selected" : ""}
            onClick={() => setT(x)}
            key={x}
          >
            {x}
          </button>
        ))}
      </div>
      {message ? <div className="func-admin-note">{message}</div> : null}
      <DuplicateLoadRecovery
        loads={data.loads}
        saved={load}
        setMessage={setMessage}
      />
      {t === "Overview" ? (
        <>
          <div className="func-heading">
            <div>
              <h1>Dispatch overview</h1>
              <p>
                Live orders, pallets, runner work and active loading positions.
              </p>
            </div>
          </div>
          <div className="func-kpis">
            <Kpi label="Orders" value={data.orders.length} />
            <Kpi label="Pallets" value={data.pallets.length} />
            <Kpi
              label="Waiting for runner"
              value={
                data.jobs.filter((x: any) => x.status === "WAITING").length
              }
            />
            <Kpi label="Active loads" value={data.loads.length} />
          </div>
          <List
            title="Current pallets"
            columns={["Pallet", "Order", "Customer", "Status", "Location"]}
            rows={data.pallets
              .slice(0, 20)
              .map((x: any) => [
                x.sscc,
                x.order_number,
                x.customer,
                x.status,
                x.location || "Not staged",
              ])}
            empty="No pallets found."
          />
        </>
      ) : t === "Order input" ? (
        <DispatchOrderInput saved={load} setMessage={setMessage} />
      ) : t === "Pick queue" ? (
        <DispatchPickQueue
          pallets={data.pallets}
          saved={load}
          setMessage={setMessage}
        />
      ) : t === "Runner queue" ? (
        <DispatchRunner
          jobs={data.jobs}
          loads={data.loads}
          saved={load}
          setMessage={setMessage}
        />
      ) : t === "Loads" ? (
        <DispatchLoads
          loads={data.loads}
          saved={load}
          setMessage={setMessage}
        />
      ) : t === "Holding areas" ? (
        <HoldingAreasWorkspace setMessage={setMessage} />
      ) : (
        <DispatchSearch setMessage={setMessage} />
      )}
    </>
  );
}
function DuplicateLoadRecovery({ loads, saved, setMessage }: any) {
  const duplicated = new Set(
    loads
      .filter(
        (x: any) =>
          loads.filter((y: any) => y.destination_code === x.destination_code)
            .length > 1,
      )
      .map((x: any) => x.destination_code),
  );
  if (!duplicated.size) return null;
  async function act(load: any, action: string) {
    if (
      action === "assign" &&
      !confirm(
        `Confirm that trailer ${load.trailer_number} physically contains the unassigned pallets at ${load.destination_code}.`,
      )
    )
      return;
    const r = await request(
        `/api/dispatch/loads/${load.id}/${action === "assign" ? "assign-unassigned" : "close-duplicate"}`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: "{}",
        },
      ),
      x = await r.json();
    setMessage(x.message || x.error);
    if (r.ok) saved();
  }
  return (
    <section className="duplicate-recovery">
      <h2>Duplicate loading position requires attention</h2>
      <p>
        Select the trailer that physically contains the existing pallets, then
        close the other empty duplicate.
      </p>
      {loads
        .filter((x: any) => duplicated.has(x.destination_code))
        .map((x: any) => (
          <div key={x.id}>
            <strong>
              {x.destination_code} · Trailer {x.trailer_number}
            </strong>
            <span>
              {x.loaded_count} assigned · {x.unassigned_count} awaiting
              assignment
            </span>
            <button onClick={() => act(x, "assign")}>
              This trailer contains the pallets
            </button>
            <button onClick={() => act(x, "close")}>
              Close empty duplicate
            </button>
          </div>
        ))}
    </section>
  );
}
function DispatchOrderInput({ saved, setMessage }: any) {
  const [f, setF] = useState({
    number: "", customer: "", routeCode: "AD", palletCount: 1,
    boxes: 0, totalWeightKg: 0, collection: false,
    pickDate: new Date().toISOString().slice(0, 10),
  });
  const [routeCodes, setRouteCodes] = useState<string[]>([]);
  const [newCode, setNewCode] = useState("");
  const [orders, setOrders] = useState<any[]>([]);
  useEffect(() => {
    request("/api/dispatch/route-codes").then(async r => { if (r.ok) { const d = await r.json(); setRouteCodes(d.codes); if (d.codes.length) setF(f => ({...f, routeCode: d.codes[0]})); } });
    request("/api/dispatch/bootstrap").then(async r => { if (r.ok) { const d = await r.json(); setOrders(d.orders.filter((o: any) => o.status !== "CANCELLED")); } });
  }, []);
  async function submit(e: FormEvent) {
    e.preventDefault();
    const r = await request("/api/dispatch/orders", {
      method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(f),
    }), x = await r.json();
    setMessage(r.ok ? `Created ${x.number}: ${x.palletIds.join(", ")}` : x.error.replaceAll("_", " "));
    if (r.ok) { setF({ ...f, number: "", customer: "" }); saved(); }
  }
  async function cancelOrder(id: string, number: string) {
    if (!confirm(`Cancel order ${number}? This cannot be undone if pallets have not been staged.`)) return;
    const r = await request(`/api/dispatch/orders/${id}`, { method: "DELETE" }), x = await r.json();
    setMessage(r.ok ? `Order ${number} cancelled.` : x.error.replaceAll("_", " "));
    if (r.ok) saved();
  }
  return (
    <>
      <section className="func-panel">
        <div className="func-table-head"><h2>Create dispatch order</h2></div>
        <form className="func-brand-form" onSubmit={submit}>
          <label>Order number<input required value={f.number} onChange={e => setF({ ...f, number: e.target.value.toUpperCase() })} /></label>
          <label>Customer<input required value={f.customer} onChange={e => setF({ ...f, customer: e.target.value })} /></label>
          <label>Route
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <select value={f.routeCode} onChange={e => setF({ ...f, routeCode: e.target.value })}>
                {routeCodes.map(c => <option key={c}>{c}</option>)}
              </select>
              <input placeholder="Add route…" value={newCode} onChange={e => setNewCode(e.target.value.toUpperCase())} style={{width:110}} />
              {newCode && <button type="button" className="secondary" onClick={() => { setRouteCodes(r => [...new Set([...r, newCode])]); setF(f => ({...f, routeCode: newCode})); setNewCode(""); }}>Add</button>}
            </div>
          </label>
          <label>Pick date<input type="date" value={f.pickDate} onChange={e => setF({ ...f, pickDate: e.target.value })} /></label>
          <label>Pallets<input type="number" min="1" max="999" value={f.palletCount} onChange={e => setF({ ...f, palletCount: Number(e.target.value) })} /></label>
          <label>Boxes<input type="number" min="0" value={f.boxes} onChange={e => setF({ ...f, boxes: Number(e.target.value) })} /></label>
          <label>Total weight kg<input type="number" min="0" step="0.001" value={f.totalWeightKg} onChange={e => setF({ ...f, totalWeightKg: Number(e.target.value) })} /></label>
          <label><input type="checkbox" checked={f.collection} onChange={e => setF({ ...f, collection: e.target.checked })} /> Customer collection</label>
          <button>Create order and pallets</button>
        </form>
      </section>
      {orders.length > 0 && (
        <section className="func-panel">
          <div className="func-table-head"><h2>Active orders</h2></div>
          <div className="func-list" style={{"--cols":"1fr 1fr 1fr 1fr auto"} as any}>
            <div className="func-list-row func-th"><span>Order</span><span>Customer</span><span>Route</span><span>Status</span><span></span></div>
            {orders.slice(0, 30).map((o: any) => (
              <div className="func-list-row" key={o.id}>
                <span>{o.number}</span><span>{o.customer}</span><span>{o.route_code}</span><span>{o.status}</span>
                <button className="secondary" onClick={() => cancelOrder(o.id, o.number)}>Cancel</button>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
function DispatchPickQueue({ pallets, saved, setMessage }: any) {
  const [sscc, setSscc] = useState(""),
    [location, setLocation] = useState("PICKED-61");
  async function submit(e: FormEvent) {
    e.preventDefault();
    const r = await request(`/api/dispatch/pallets/${encodeURIComponent(sscc)}/stage`, {
      method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ location }),
    }), x = await r.json();
    setMessage(r.ok ? x.message : x.error);
    if (r.ok) { setSscc(""); saved(); }
  }
  function downloadLabel(sscc: string) {
    const a = document.createElement("a");
    a.href = `/api/dispatch/pallets/${encodeURIComponent(sscc)}/qr.svg`;
    a.download = `pallet-${sscc}.svg`;
    a.click();
  }
  const waiting = pallets.filter((x: any) => x.status === "CREATED");
  return (
    <>
      <section className="func-panel">
        <div className="func-table-head"><h2>Stage a picked pallet</h2></div>
        <form className="dispatch-scan" onSubmit={submit}>
          <input autoFocus placeholder="Scan pallet barcode" value={sscc} onChange={e => setSscc(e.target.value.toUpperCase())} required />
          <input placeholder="Staging location" value={location} onChange={e => setLocation(e.target.value.toUpperCase())} required />
          <button>Stage pallet</button>
        </form>
      </section>
      <section className="func-panel">
        <div className="func-table-head"><h2>Awaiting staging</h2></div>
        {waiting.length === 0 ? <p className="func-empty">No pallets are awaiting staging.</p> : (
          <div className="func-list" style={{"--cols":"1fr 1fr 1fr 1fr auto"} as any}>
            <div className="func-list-row func-th"><span>Pallet</span><span>Order</span><span>Customer</span><span>Status</span><span></span></div>
            {waiting.map((x: any) => (
              <div className="func-list-row" key={x.id}>
                <span>{x.sscc}</span><span>{x.order_number}</span><span>{x.customer}</span><span>{x.status}</span>
                <button className="secondary" onClick={() => downloadLabel(x.sscc)}><QrCode size={13} /> Label</button>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
function DispatchRunner({ jobs, loads, saved, setMessage }: any) {
  const [barcode, setBarcode] = useState("");
  async function submit(e: FormEvent) {
    e.preventDefault();
    const r = await request("/api/dispatch/runner/scan", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ barcode }),
      }),
      x = await r.json();
    setMessage(r.ok ? x.message : x.error);
    if (r.ok) {
      setBarcode("");
      saved();
    }
  }
  return (
    <>
      <section className="func-panel">
        <div className="func-table-head">
          <h2>Two-scan runner workflow</h2>
        </div>
        <form className="dispatch-scan" onSubmit={submit}>
          <input
            autoFocus
            placeholder="Scan pallet, trailer position or PRE-LOAD"
            value={barcode}
            onChange={(e) => setBarcode(e.target.value.toUpperCase())}
          />
          <button>Process scan</button>
        </form>
      </section>
      <List
        title="Shared runner queue"
        columns={["Pallet", "Order", "From", "Destination", "Status"]}
        rows={jobs.map((x: any) => [
          x.sscc,
          x.order_number,
          x.staging_location,
          x.destination,
          x.status,
        ])}
        empty="Runner queue is clear."
      />
      <List
        title="Loading positions"
        columns={[
          "Position",
          "Trailer",
          "Route",
          "Loaded",
          "Capacity",
          "Status",
        ]}
        rows={loads.map((x: any) => [
          x.destination_code,
          x.trailer_number,
          x.route_code,
          x.loaded_count,
          x.capacity,
          x.status,
        ])}
        empty="No loading positions are open."
      />
    </>
  );
}
function DispatchLoads({ loads, saved, setMessage }: any) {
  const [f, setF] = useState({ destinationCode: "AD-1", routeCode: "AD", trailerNumber: "", capacity: 24 });
  async function submit(e: FormEvent) {
    e.preventDefault();
    const r = await request("/api/dispatch/loads", {
      method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(f),
    }), x = await r.json();
    setMessage(r.ok ? `${f.trailerNumber} assigned to ${f.destinationCode}` : (x.message || x.error));
    if (r.ok) { setF({ ...f, trailerNumber: "" }); saved(); }
  }
  async function act(id: string, action: string, label: string) {
    if (!confirm(`${label}?`)) return;
    const r = await request(`/api/dispatch/loads/${id}/${action}`, { method: "POST", headers: { "content-type": "application/json" }, body: "{}" }), x = await r.json();
    setMessage(x.message || x.error || "Done");
    if (r.ok) saved();
  }
  return (
    <>
      <section className="func-panel">
        <div className="func-table-head"><h2>Assign trailer or container</h2></div>
        <form className="func-brand-form" onSubmit={submit}>
          <label>Loading position<input value={f.destinationCode} onChange={e => setF({ ...f, destinationCode: e.target.value.toUpperCase() })} /></label>
          <label>Route<input value={f.routeCode} onChange={e => setF({ ...f, routeCode: e.target.value.toUpperCase() })} /></label>
          <label>Trailer/container number<input required value={f.trailerNumber} onChange={e => setF({ ...f, trailerNumber: e.target.value.toUpperCase() })} /></label>
          <label>Capacity<input type="number" min="1" value={f.capacity} onChange={e => setF({ ...f, capacity: Number(e.target.value) })} /></label>
          <button>Open loading position</button>
        </form>
      </section>
      <section className="func-panel">
        <div className="func-table-head"><h2>Open loads</h2></div>
        {loads.length === 0 ? <p className="func-empty">No loads open.</p> : (
          <div className="func-list" style={{"--cols":"1fr 1fr 1fr 1fr 1fr auto"} as any}>
            <div className="func-list-row func-th"><span>Position</span><span>Trailer</span><span>Route</span><span>Loaded / Cap</span><span>Status</span><span></span></div>
            {loads.map((x: any) => (
              <div className="func-list-row" key={x.id}>
                <span>{x.destination_code}</span>
                <span>{x.trailer_number}</span>
                <span>{x.route_code}</span>
                <span>{x.loaded_count} / {x.capacity}</span>
                <span>{x.status}</span>
                <div style={{display:"flex",gap:6}}>
                  {x.status === "LOADING" && <button className="secondary" onClick={() => act(x.id, "full", `Mark trailer ${x.trailer_number} as full`)}>Mark full</button>}
                  <button className="secondary" onClick={() => { const el = document.createElement("a"); el.href = `/api/dispatch/pallets/${x.destination_code}/qr.svg`; el.download = `load-${x.destination_code}.svg`; el.click(); }}>Label</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
function DispatchSearch({ setMessage }: any) {
  const [term, setTerm] = useState(""),
    [result, setResult] = useState<any>(null);
  async function submit(e: FormEvent) {
    e.preventDefault();
    const r = await request(`/api/dispatch/status/${encodeURIComponent(term)}`), x = await r.json();
    if (r.ok) setResult(x);
    else { setResult(null); setMessage(x.error); }
  }
  // Build SSCC lookup from pallets for movement history display
  const ssccMap = result ? Object.fromEntries(result.pallets.map((p: any) => [p.id, p.sscc])) : {};
  return (
    <>
      <section className="func-panel">
        <div className="func-table-head"><h2>Order and pallet search</h2></div>
        <form className="dispatch-scan" onSubmit={submit}>
          <input placeholder="Order number or pallet barcode" value={term} onChange={e => setTerm(e.target.value.toUpperCase())} />
          <button>Search</button>
        </form>
      </section>
      {result ? (
        <>
          <div className="func-kpis" style={{marginBottom:0}}>
            <Kpi label="Pallets" value={result.pallets.length} />
            <Kpi label="Dispatched" value={result.pallets.filter((p: any) => p.status === "DISPATCHED").length} />
            <Kpi label="Loaded" value={result.pallets.filter((p: any) => p.status === "LOADED").length} />
            <Kpi label="In transit" value={result.pallets.filter((p: any) => ["IN_TRANSIT","STAGED","PRE_LOAD"].includes(p.status)).length} />
          </div>
          <List
            title={`${result.order.number} — ${result.order.customer} · ${result.order.route_code}`}
            columns={["Pallet SSCC", "Status", "Location"]}
            rows={result.pallets.map((x: any) => [x.sscc, x.status.replaceAll("_", " "), x.location || "—"])}
            empty="No pallets."
          />
          <List
            title="Movement history"
            columns={["Time", "Pallet", "From", "To", "Result", "Actor"]}
            rows={result.history.map((x: any) => [
              new Date(x.created_at).toLocaleString("en-GB", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" }),
              ssccMap[x.pallet_id] || x.pallet_id,
              x.from_location || "—",
              x.to_location,
              x.result.replaceAll("_", " "),
              x.actor || "System",
            ])}
            empty="No movements recorded."
          />
        </>
      ) : null}
    </>
  );
}
function DispatchReports() {
  const [data, setData] = useState<any>(null);
  useEffect(() => { request("/api/dispatch/reports").then(async r => { if (r.ok) setData(await r.json()); }); }, []);
  if (!data) return <p className="func-loading">Loading reports…</p>;
  const s = data.summary?.[0] ?? {};
  return (
    <>
      <div className="func-heading">
        <div><h1>Dispatch reports</h1><p>Orders, pallets and manifests overview.</p></div>
        <a className="func-button" href={`${apiOrigin}/api/exports/complete`}><BarChart3 />Complete export</a>
      </div>
      <div className="func-kpis">
        <Kpi label="Total orders" value={s.total_orders ?? 0} />
        <Kpi label="Total pallets" value={s.total_pallets ?? 0} />
        <Kpi label="Dispatched" value={s.dispatched ?? 0} />
        <Kpi label="Active" value={s.active ?? 0} />
        <Kpi label="Collections" value={s.collections ?? 0} />
      </div>
      {(data.byRoute || []).length > 0 && (
        <section className="func-panel" style={{marginTop:16}}>
          <div className="func-table-head"><h2>By route code</h2></div>
          <div className="func-list" style={{"--cols":"1fr 1fr 1fr 1fr"} as any}>
            <div className="func-list-row func-th"><span>Route</span><span>Orders</span><span>Pallets</span><span>Dispatched</span></div>
            {data.byRoute.map((r: any) => (
              <div className="func-list-row" key={r.route_code}>
                <span>{r.route_code}</span><span>{r.orders}</span><span>{r.pallets}</span>
                <span style={{color: Number(r.dispatched) === Number(r.pallets) ? "var(--green)" : undefined}}>{r.dispatched}</span>
              </div>
            ))}
          </div>
        </section>
      )}
      {(data.recentManifests || []).length > 0 && (
        <section className="func-panel" style={{marginTop:16}}>
          <div className="func-table-head"><h2>Recent manifests</h2></div>
          <div className="func-list" style={{"--cols":"1fr 1fr 1fr 1fr 1fr 1fr"} as any}>
            <div className="func-list-row func-th"><span>Reference</span><span>Destination</span><span>Trailer</span><span>Driver</span><span>Pallets</span><span>Status</span></div>
            {data.recentManifests.map((m: any) => (
              <div className="func-list-row" key={m.reference}>
                <span>{m.reference}</span><span>{m.destination_code}</span><span>{m.trailer_number}</span>
                <span>{m.driver_name || "—"}</span><span>{m.pallets}</span>
                <span className={m.status === "CLOSED" ? "active" : ""}>{m.status}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}

function BrandingSettings() {
  const [form, setForm] = useState({
      logoUrl: "",
      primaryColor: "#102a43",
      accentColor: "#12a594",
      signInHeading: "",
      welcomeMessage: "",
    }),
    [message, setMessage] = useState("");
  useEffect(() => {
    request("/api/branding").then(async (r) => {
      if (r.ok) {
        const b = await r.json();
        setForm({
          logoUrl: b.logoUrl || "",
          primaryColor: b.primaryColor,
          accentColor: b.accentColor,
          signInHeading: b.signInHeading,
          welcomeMessage: b.welcomeMessage,
        });
      }
    });
  }, []);
  async function save(e: FormEvent) {
    e.preventDefault();
    setMessage("Saving…");
    const r = await request("/api/branding", {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(form),
    });
    setMessage(
      r.ok
        ? "Branding saved. The login page will update immediately."
        : "Only the tenant owner can update branding.",
    );
  }
  return (
    <section className="func-panel">
      <div className="func-table-head">
        <h2>Client login branding</h2>
      </div>
      <form className="func-brand-form" onSubmit={save}>
        <label>
          Logo URL (HTTPS)
          <input
            type="url"
            value={form.logoUrl}
            onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
            placeholder="https://example.com/logo.png"
          />
        </label>
        <label>
          Sign-in heading
          <input
            value={form.signInHeading}
            onChange={(e) =>
              setForm({ ...form, signInHeading: e.target.value })
            }
            maxLength={120}
          />
        </label>
        <label>
          Welcome text
          <input
            value={form.welcomeMessage}
            onChange={(e) =>
              setForm({ ...form, welcomeMessage: e.target.value })
            }
            maxLength={300}
          />
        </label>
        <label>
          Primary colour
          <input
            type="color"
            value={form.primaryColor}
            onChange={(e) => setForm({ ...form, primaryColor: e.target.value })}
          />
        </label>
        <label>
          Accent colour
          <input
            type="color"
            value={form.accentColor}
            onChange={(e) => setForm({ ...form, accentColor: e.target.value })}
          />
        </label>
        <button>Save login branding</button>
        {message ? <span>{message}</span> : null}
      </form>
    </section>
  );
}
function DispatchOrders({ orders }: { orders: any[] }) {
  return (
    <List
      title="Dispatch orders"
      columns={["Order", "Customer", "Status", "Collection"]}
      rows={orders.map((x: any) => [
        x.number,
        x.customer,
        x.status,
        x.collection ? "Yes" : "No",
      ])}
      empty="No dispatch orders found."
    />
  );
}
function Kpi({ label, value }: { label: string; value: number }) {
  return (
    <div className="func-kpi">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
function WorkTable({ rows, query, setQuery }: any) {
  return (
    <section className="func-panel">
      <div className="func-table-head">
        <h2>Work orders</h2>
        <label>
          <Search />
          <input
            placeholder="Search work orders"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
      </div>
      <div className="func-table">
        <div className="func-row func-th">
          <span>Number</span>
          <span>Issue</span>
          <span>Priority</span>
          <span>Status</span>
          <span>Reported</span>
        </div>
        {rows.map((w: any) => (
          <div className="func-row" key={w.id}>
            <b>{w.number}</b>
            <span>{w.title}</span>
            <span>{w.priority.replaceAll("_", " ")}</span>
            <span>
              <em>{w.status.replaceAll("_", " ")}</em>
            </span>
            <span>{new Date(w.reportedAt).toLocaleDateString()}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
function List({
  title,
  columns,
  rows,
  empty,
}: {
  title: string;
  columns: string[];
  rows: any[][];
  empty: string;
}) {
  return (
    <section className="func-panel">
      <div className="func-table-head">
        <h2>{title}</h2>
      </div>
      <div className="func-list" style={{ "--cols": columns.length } as any}>
        <div className="func-list-row func-th">
          {columns.map((c) => (
            <span key={c}>{c}</span>
          ))}
        </div>
        {rows.length ? (
          rows.map((r, i) => (
            <div className="func-list-row" key={i}>
              {r.map((v, j) => (
                <span key={j}>{String(v)}</span>
              ))}
            </div>
          ))
        ) : (
          <p className="func-empty">{empty}</p>
        )}
      </div>
    </section>
  );
}
function CreateWorkOrder({ assets, close, saved }: any) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("MEDIUM");
  const [assetId, setAssetId] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    const r = await request("/api/work-orders", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        title,
        description,
        priority,
        assetId: assetId || null,
      }),
    });
    setBusy(false);
    if (r.ok) saved();
  }
  return (
    <div className="func-modal">
      <form onSubmit={submit}>
        <header>
          <h2>Create work order</h2>
          <button type="button" onClick={close}>
            <X />
          </button>
        </header>
        <label>
          Issue title
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </label>
        <label>
          Asset
          <select value={assetId} onChange={(e) => setAssetId(e.target.value)}>
            <option value="">No asset selected</option>
            {assets.map((a: Asset) => (
              <option value={a.id} key={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Priority
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          >
            <option value="LOW">Low</option>
            <option value="MEDIUM">Medium</option>
            <option value="HIGH">High</option>
            <option value="PRODUCTION_CAN_RUN">Production can run</option>
            <option value="PRODUCTION_STOPPED">Production stopped</option>
          </select>
        </label>
        <label>
          Description
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        <footer>
          <button type="button" className="secondary" onClick={close}>
            Cancel
          </button>
          <button disabled={busy}>
            {busy ? "Saving…" : "Create work order"}
          </button>
        </footer>
      </form>
    </div>
  );
}
